import { Avatar, Box, Card, CardContent, Skeleton, Stack, Typography, alpha } from '@mui/material';
import type { ReactNode } from 'react';

export type StatCardTone = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

const TONE_GRADIENT: Record<StatCardTone, string> = {
  primary: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)',
  success: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
  warning: 'linear-gradient(135deg, #D97706 0%, #B45309 100%)',
  error:   'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
  info:    'linear-gradient(135deg, #0284C7 0%, #0369A1 100%)',
  neutral: 'linear-gradient(135deg, #64748B 0%, #475569 100%)',
};

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  change?: string;
  icon: ReactNode;
  tone?: StatCardTone;
  loading?: boolean;
}

export function StatCard({
  title,
  value,
  subtitle,
  change,
  icon,
  tone = 'primary',
  loading = false,
}: StatCardProps) {
  const gradient = TONE_GRADIENT[tone];

  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(15,23,42,0.10)',
          transform: 'translateY(-1px)',
        },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:hover': { transform: 'none' },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -24,
          right: -24,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: gradient,
          opacity: 0.1,
          filter: 'blur(20px)',
          pointerEvents: 'none',
        }}
      />
      <CardContent sx={{ height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="overline"
              sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontSize: '0.7rem' }}
            >
              {title}
            </Typography>
            {loading ? (
              <Skeleton width={80} height={44} />
            ) : (
              <Typography
                sx={{
                  fontSize: { xs: '1.75rem', sm: '2rem' },
                  fontWeight: 800,
                  color: 'text.primary',
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                }}
              >
                {value}
              </Typography>
            )}
            {(subtitle || change) && !loading && (
              <Typography
                sx={{ fontSize: '0.78rem', fontWeight: 500, color: 'text.secondary', mt: 0.75 }}
              >
                {subtitle ?? change}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              background: gradient,
              boxShadow: (t) => `0 8px 20px ${alpha(t.palette.common.black, 0.12)}`,
              borderRadius: '14px',
              flexShrink: 0,
              '& .MuiSvgIcon-root': { fontSize: 22, color: '#fff' },
            }}
          >
            {icon}
          </Avatar>
        </Stack>
      </CardContent>
    </Card>
  );
}
